// The diary editor's two 「來源」 pickers.
//
// W54.0 A1 moved them out of `components/diary/DiaryEditor.tsx` when that file
// was split to get under the `AGENTS.md §3 #10` ceiling. They are create-mode
// only, they are siblings, and they are the two heaviest blocks of JSX in the
// editor — so they are one component rather than two.
//
// The pickers are mutually exclusive by payload, not by UI: the diary carries
// one `source_type`, so choosing a wish clears the anniversary. That rule
// lives with the state (`lib/diary/formState.ts`); this component only renders
// the two selects and reports what was picked.

import type { DiarySourceOption, DiaryWishOption } from "@/lib/diary/formState";

export interface DiarySourceFieldsProps {
  anniversaries: DiarySourceOption[];
  anniversaryId: string | null;
  onAnniversaryChange: (id: string | null) => void;
  wishes: DiaryWishOption[];
  wishId: string | null;
  onWishChange: (id: string | null) => void;
  disabled?: boolean;
}

export function DiarySourceFields({
  anniversaries,
  anniversaryId,
  onAnniversaryChange,
  wishes,
  wishId,
  onWishChange,
  disabled,
}: DiarySourceFieldsProps) {
  return (
    <>
      {anniversaries.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="diary-source-anniversary" className="font-label text-ink-2">
            來源 → 紀念日 (可選)
          </label>
          <select
            id="diary-source-anniversary"
            value={anniversaryId ?? ""}
            onChange={(e) => onAnniversaryChange(e.target.value || null)}
            disabled={disabled}
            data-testid="diary-source-anniversary"
            // Per W35.5 follow-up issue #6: native `<select>` chevron
            // used to overlap the rounded border. `appearance-none`
            // + `pr-10` reserves space for a custom SVG chevron, plus
            // focus-ring stays consistent with `<input>`.
            className="h-12 appearance-none rounded-md border border-hairline bg-paper pl-3 pr-10 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2 bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2020%2020%22%20fill=%22%23a3a3a3%22><path%20d=%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.06l3.71-3.83a.75.75%200%20011.08%201.03l-4.25%204.39a.75.75%200%0001-1.08%200L5.21%208.27a.75.75%200%0001.02-1.06z%22/></svg>')] bg-[length:20px_20px] bg-[right_0.75rem_center] bg-no-repeat"
          >
            <option value="">手寫 (不關聯任何紀念日)</option>
            {anniversaries.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title} · {a.date}
              </option>
            ))}
          </select>
          <p className="text-meta text-ink-3">
            這篇日記會與紀念日關聯, 在該紀念日的「相關日記」可見
          </p>
        </div>
      ) : null}

      {/* W50.3 D-3 — 「來源 → 心願」, parallel to the anniversary select above.
          Create-mode only, for the same reason: `DiaryUpdatePayload` does not
          carry `source_type`/`source_id` on purpose — the PATCH body is dumped
          with `exclude_none=True`, so a null can never arrive and an unlink
          smuggled through PATCH would be indistinguishable from a no-op. That
          is why `PUT /diaries/{id}/source` exists as its own route. Attaching
          an existing diary to a wish after the fact goes through
          `components/wish/WishDiaryLinks.tsx` from the wish side. */}
      {wishes.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="diary-source-wish" className="font-label text-ink-2">
            來源 → 心願 (可選)
          </label>
          <select
            id="diary-source-wish"
            value={wishId ?? ""}
            onChange={(e) => onWishChange(e.target.value || null)}
            disabled={disabled}
            data-testid="diary-source-wish"
            className="h-12 appearance-none rounded-md border border-hairline bg-paper pl-3 pr-10 text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2 bg-[url('data:image/svg+xml;utf8,<svg%20xmlns=%22http://www.w3.org/2000/svg%22%20viewBox=%220%200%2020%2020%22%20fill=%22%23a3a3a3%22><path%20d=%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.06l3.71-3.83a.75.75%200%20011.08%201.03l-4.25%204.39a.75.75%200%0001-1.08%200L5.21%208.27a.75.75%200%0001.02-1.06z%22/></svg>')] bg-[length:20px_20px] bg-[right_0.75rem_center] bg-no-repeat"
          >
            <option value="">手寫 (不關聯任何心願)</option>
            {wishes.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
          <p className="text-meta text-ink-3">
            這篇日記會出現在該心願的「連結的日記」裡
          </p>
        </div>
      ) : null}
    </>
  );
}
